import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "../components/PageTitle";
import { useCartStore } from "../store/cartStore";
import {
  applyCoupon,
  createAddress,
  createOrder,
  createPaymentLink,
  deleteAddress,
  getAddresses,
} from "../api/checkout";
import { getImageUrl } from "../api/utils";
import { sizesLabel, stripEmojis } from "../utils";
import toast from "react-hot-toast";

// ── Constants ─────────────────────────────────────────────────
const BLANK_ADDRESS = {
  name: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  is_default: false,
};

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra & Nagar Haveli",
  "Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const CheckoutV2 = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("Token");
  const isAuthenticated = Boolean(token);

  const initCart = useCartStore((s) => s.initCart);
  const fetchCart = useCartStore((s) => s.fetchCart);
  const cartData = useCartStore((s) => s.cartData);
  const loading = useCartStore((s) => s.loading);
  const cartId = useCartStore((s) => s.cartId);
  const currentCartId = cartData?.id || cartData?.cart_id || cartId;

  // ── State ────────────────────────────────────────────────────
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState(BLANK_ADDRESS);
  const [addressErrors, setAddressErrors] = useState({});
  const [savingAddress, setSavingAddress] = useState(false);
  const [fetchingAddresses, setFetchingAddresses] = useState(true);

  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const [notes, setNotes] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("prepaid");

  const COD_CHARGE = 49;

  const hasLoadedAddressesRef = useRef(false);
  const handledSessionTimeoutRef = useRef(false);

  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestAddress, setGuestAddress] = useState(BLANK_ADDRESS);
  const [guestErrors, setGuestErrors] = useState({});

  // ── Init cart ────────────────────────────────────────────────
  useEffect(() => {
    initCart();
  }, [initCart]);
  useEffect(() => {
    if (isAuthenticated && !cartData) fetchCart();
  }, [cartData, fetchCart, isAuthenticated]);

  // ── Restore coupon from session ──────────────────────────────
  useEffect(() => {
    const saved = sessionStorage.getItem("hh_coupon");
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.code) {
        setCouponCode(parsed.code);
        setCouponApplied(parsed);
      }
    } catch {
      sessionStorage.removeItem("hh_coupon");
    }
  }, []);

  // ── Load addresses ───────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || hasLoadedAddressesRef.current) return;
    hasLoadedAddressesRef.current = true;
    const load = async () => {
      setFetchingAddresses(true);
      try {
        const res = await getAddresses();
        const list = res?.addresses || [];
        setAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) setSelectedAddressId(def.id);
      } catch (err) {
        handleSessionTimeout(err);
        if (err !== "sessionTimeout") toast.error("Could not load addresses");
      } finally {
        setFetchingAddresses(false);
      }
    };
    load();
  }, [isAuthenticated]);

  // ── Session timeout helper ────────────────────────────────────
  const handleSessionTimeout = (err) => {
    if (err === "sessionTimeout" && !handledSessionTimeoutRef.current) {
      handledSessionTimeoutRef.current = true;
      localStorage.removeItem("Token");
      navigate("/login", {
        replace: true,
        state: {
          from: "/checkout",
          message: "Please login again to continue checkout",
        },
      });
    }
  };

  // ── Order items ───────────────────────────────────────────────
  const orderItems = useMemo(() => {
    return (cartData?.items || []).map((item) => ({
      id: item.id,
      title: stripEmojis(item.product_name || item.name || "Cart Item"),
      variant: [
        item.variant_color || item.color,
        item.variant_size || sizesLabel[item.size],
      ]
        .filter(Boolean)
        .join(" / "),
      price: Number(item.price || item.product_price || 0),
      quantity: Number(item.quantity || 1),
      image:
        getImageUrl(
          item.image_url || item.display_image || item.product_image || "",
        ) || "/assets/images/product-placeholder.svg",
    }));
  }, [cartData]);

  // ── Totals ────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => orderItems.reduce((s, i) => s + i.price * i.quantity, 0),
    [orderItems],
  );
  const shipping = subtotal > 0 && subtotal < 499 ? 70 : 0; // Free above ₹499
  const discount = Number(couponApplied?.discount_amount || 0);
  const codCharge = paymentMethod === "cod" ? COD_CHARGE : 0;
  const total = Math.max(subtotal + shipping + codCharge - discount, 0);

  // ── Address handlers ──────────────────────────────────────────
  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (addressErrors[name])
      setAddressErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateAddressForm = () => {
    const errs = {};
    if (!addressForm.name.trim()) errs.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(addressForm.phone))
      errs.phone = "Enter valid 10-digit mobile number";
    if (!addressForm.address_line1.trim())
      errs.address_line1 = "Street address is required";
    if (!addressForm.city.trim()) errs.city = "City is required";
    if (!addressForm.state.trim()) errs.state = "State is required";
    if (!/^[1-9][0-9]{5}$/.test(addressForm.pincode))
      errs.pincode = "Enter valid 6-digit PIN code";
    return errs;
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    const errs = validateAddressForm();
    if (Object.keys(errs).length > 0) {
      setAddressErrors(errs);
      return;
    }

    setSavingAddress(true);
    try {
      const res = await createAddress(addressForm);
      const saved = res?.address;
      if (saved?.id) setSelectedAddressId(saved.id);
      setAddressForm(BLANK_ADDRESS);
      setAddressErrors({});
      setShowAddressModal(false);
      const refreshed = await getAddresses();
      setAddresses(refreshed?.addresses || []);
      toast.success("Address saved!");
    } catch (err) {
      handleSessionTimeout(err);
      if (err !== "sessionTimeout")
        toast.error(err?.message || "Could not save address");
    } finally {
      setSavingAddress(false);
    }
  };

  const removeAddress = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    try {
      await deleteAddress(id);
      const refreshed = await getAddresses();
      const list = refreshed?.addresses || [];
      setAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      setSelectedAddressId(def?.id || null);
      toast.success("Address removed");
    } catch (err) {
      handleSessionTimeout(err);
      if (err !== "sessionTimeout")
        toast.error(err?.message || "Could not remove address");
    }
  };

  // ── Coupon ────────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!currentCartId) {
      toast.error("Cart is not ready yet. Please try again.");
      return;
    }
    setCouponLoading(true);
    try {
      const code = couponCode.trim().toUpperCase();
      const res = await applyCoupon(
        code,
        currentCartId,
        !isAuthenticated ? guestEmail : null,
      );

      const applied = { code, discount_amount: res?.discount_amount || 0 };
      setCouponApplied(applied);
      sessionStorage.setItem("hh_coupon", JSON.stringify(applied));
      toast.success(res?.message || "Coupon applied!");
    } catch (err) {
      handleSessionTimeout(err);
      if (err !== "sessionTimeout")
        toast.error(typeof err === "string" ? err : "Invalid coupon code");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponCode("");
    sessionStorage.removeItem("hh_coupon");
  };

  // ── Place order ───────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    // ── Guest validation ───────────────────────────────────
    if (!isAuthenticated) {
      const errs = {};
      if (!guestEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail))
        errs.email = "Valid email is required";
      if (!guestName.trim()) errs.name = "Name is required";
      if (!/^[6-9]\d{9}$/.test(guestPhone))
        errs.phone = "Valid 10-digit phone required";
      if (!guestAddress.address_line1.trim())
        errs.address_line1 = "Address is required";
      if (!guestAddress.city.trim()) errs.city = "City is required";
      if (!guestAddress.state.trim()) errs.state = "State is required";
      if (!/^[1-9][0-9]{5}$/.test(guestAddress.pincode))
        errs.pincode = "Valid PIN required";

      if (Object.keys(errs).length > 0) {
        setGuestErrors(errs);
        return;
      }
    }

    if (isAuthenticated && !selectedAddressId) {
      toast.error("Please select a delivery address");
      return;
    }

    setPlacingOrder(true);
    try {
      const orderPayload = isAuthenticated
        ? {
            cart_id: currentCartId,
            address_id: selectedAddressId,
            coupon_code: couponApplied?.code || null,
            notes: notes || null,
            payment_method: paymentMethod,
          }
        : {
            cart_id: currentCartId,
            guest_email: guestEmail.trim(),
            guest_name: guestName.trim(),
            guest_phone: guestPhone.trim(),
            coupon_code: couponApplied?.code || null,
            notes: notes || null,
            payment_method: paymentMethod,
            // ── Address inline pass karo ──────────────────
            guest_address: {
              name: guestName.trim(),
              phone: guestPhone.trim(),
              address_line1: guestAddress.address_line1,
              address_line2: guestAddress.address_line2,
              city: guestAddress.city,
              state: guestAddress.state,
              pincode: guestAddress.pincode,
            },
          };

      const orderRes = await createOrder(orderPayload);
      const { order_number } = orderRes || {};
      if (!order_number) throw new Error("Order could not be created");

      sessionStorage.removeItem("hh_coupon");

      if (paymentMethod === "cod") {
        navigate(`/order-success?order=${order_number}`);
        return;
      }

      const linkRes = await createPaymentLink(order_number);
      const encodedLink = encodeURIComponent(linkRes.payment_link_url);
      window.location.href = `/payment-redirect?link=${encodedLink}&order=${order_number}`;
    } catch (err) {
      // ── Sirf toast — navigate nahi ──────────────────────
      handleSessionTimeout(err);
      if (err !== "sessionTimeout") {
        toast.error(
          typeof err === "string"
            ? err
            : "Could not place order. Please try again.",
        );
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  // if (!isAuthenticated) return null;

  return (
    <>
      <PageTitle
        title="Check Out"
        subtitle="Almost there! Your little one's mood is just a click away!"
        bgImage="default.png"
      />

      <section className="flat-spacing-11">
        <div className="container">
          <div className="tf-page-cart-wrap layout-2">
            {/* ── LEFT: Billing & Address ── */}
            <div className="tf-page-cart-item">
              {isAuthenticated ? (
                <>
                  <h5 className="fw-5 mb_20">Billing details</h5>

                  <div className="mb_24 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <div className="fw-5 mb_5">Delivery address</div>
                      <div className="text_black-2">
                        {selectedAddressId
                          ? "Selected from your saved addresses"
                          : "No address selected yet"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="tf-btn btn-line"
                      onClick={() => {
                        setShowAddressModal(true);
                        setAddressForm(BLANK_ADDRESS);
                        setAddressErrors({});
                      }}
                    >
                      Add new address
                    </button>
                  </div>

                  <div className="mb_24">
                    <h6 className="fw-5 mb_16">Saved addresses</h6>
                    {fetchingAddresses ? (
                      <p style={{ color: "#888", fontSize: 14 }}>
                        Loading addresses...
                      </p>
                    ) : (
                      <div className="d-flex flex-column gap-12">
                        {addresses.length > 0 ? (
                          addresses.map((addr) => (
                            <div
                              key={addr.id}
                              style={{
                                border: `2px solid ${selectedAddressId === addr.id ? "#2c3a34" : "#e7e7e7"}`,
                                borderRadius: 14,
                                padding: 16,
                                background:
                                  selectedAddressId === addr.id
                                    ? "#f8f8f6"
                                    : "#fff",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <label
                                  className="d-flex gap-10 align-items-start"
                                  style={{ cursor: "pointer", flex: 1 }}
                                >
                                  <input
                                    type="radio"
                                    checked={selectedAddressId === addr.id}
                                    onChange={() =>
                                      setSelectedAddressId(addr.id)
                                    }
                                    style={{
                                      marginTop: 3,
                                      accentColor: "#2c3a34",
                                    }}
                                  />
                                  <span>
                                    <strong>{addr.name}</strong>
                                    {addr.is_default && (
                                      <span
                                        style={{
                                          marginLeft: 8,
                                          fontSize: 11,
                                          backgroundColor: "#2c3a34",
                                          color: "#fff",
                                          padding: "1px 8px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        DEFAULT
                                      </span>
                                    )}
                                    <br />
                                    <span className="text_black-2">
                                      {addr.address_line1}
                                      {addr.address_line2
                                        ? `, ${addr.address_line2}`
                                        : ""}
                                      {addr.city ? `, ${addr.city}` : ""}
                                      {addr.state ? `, ${addr.state}` : ""}
                                      {addr.pincode ? ` - ${addr.pincode}` : ""}
                                    </span>
                                    <br />
                                    <span
                                      style={{ color: "#888", fontSize: 13 }}
                                    >
                                      📞 {addr.phone}
                                    </span>
                                  </span>
                                </label>
                                <button
                                  type="button"
                                  className="tf-btn btn-line"
                                  onClick={() => removeAddress(addr.id)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              border: "1px dashed #ddd",
                              borderRadius: 14,
                              padding: 18,
                              color: "#666",
                            }}
                          >
                            No saved addresses yet. Add one to speed up
                            checkout.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div className="mb_24">
                    <h6 className="fw-5 mb_16">Additional information</h6>
                    <fieldset className="fieldset">
                      <label>
                        Order notes{" "}
                        <span style={{ color: "#999", fontWeight: 400 }}>
                          (optional)
                        </span>
                      </label>
                      <textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notes about your order, e.g. special notes for delivery."
                        style={{
                          width: "100%",
                          resize: "none",
                          border: "1px solid #ddd",
                          padding: "12px 14px",
                          color: "#2c3a34",
                        }}
                      />
                    </fieldset>
                  </div>
                </>
              ) : (
                <>
                  <h5 className="fw-5 mb_20">Contact Information</h5>
                  <p style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
                    Already have an account?{" "}
                    <a
                      href="/login"
                      style={{ color: "#2c3a34", fontWeight: 600 }}
                    >
                      Login here
                    </a>
                  </p>
                  <br />
                  {/* Email */}
                  <fieldset className="fieldset mb_16">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="your@email.com"
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: "1px solid #ddd",
                      }}
                    />
                    {guestErrors.email && (
                      <span style={{ color: "#c62828", fontSize: 12 }}>
                        {guestErrors.email}
                      </span>
                    )}
                  </fieldset>
                  <br />

                  {/* Name + Phone */}
                  <div className="box grid-2 mb_16">
                    <fieldset className="fieldset">
                      <label>Full Name *</label>
                      <input
                        style={{
                          borderRadius: 0,
                          border: "1px solid rgb(221, 221, 221)",
                        }}
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Your name"
                      />
                      {guestErrors.name && (
                        <span style={{ color: "#c62828", fontSize: 12 }}>
                          {guestErrors.name}
                        </span>
                      )}
                    </fieldset>
                    <fieldset className="fieldset">
                      <label>Phone *</label>
                      <input
                        style={{
                          borderRadius: 0,
                          border: "1px solid rgb(221, 221, 221)",
                        }}
                        type="tel"
                        maxLength={10}
                        value={guestPhone}
                        onChange={(e) =>
                          setGuestPhone(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="10-digit mobile"
                      />
                      {guestErrors.phone && (
                        <span style={{ color: "#c62828", fontSize: 12 }}>
                          {guestErrors.phone}
                        </span>
                      )}
                    </fieldset>
                  </div>
                  <br />

                  <h5 className="fw-5 mb_20">Delivery Address</h5>

                  {/* Address Line 1 */}
                  <fieldset className="box fieldset mb_16">
                    <label>Address Line 1 *</label>
                    <input
                      type="text"
                      value={guestAddress.address_line1}
                      onChange={(e) =>
                        setGuestAddress((p) => ({
                          ...p,
                          address_line1: e.target.value,
                        }))
                      }
                      placeholder="House number and street name"
                    />
                    {guestErrors.address_line1 && (
                      <span style={{ color: "#c62828", fontSize: 12 }}>
                        {guestErrors.address_line1}
                      </span>
                    )}
                  </fieldset>
                  <br />

                  {/* Address Line 2 */}
                  <fieldset className="box fieldset mb_16">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      value={guestAddress.address_line2}
                      onChange={(e) =>
                        setGuestAddress((p) => ({
                          ...p,
                          address_line2: e.target.value,
                        }))
                      }
                      placeholder="Apartment, suite, unit etc. (optional)"
                    />
                  </fieldset>
                  <br />

                  {/* City + State + Pincode */}
                  <div className="box grid-3 mb_16">
                    <fieldset className="fieldset">
                      <label>City *</label>
                      <input
                        type="text"
                        style={{
                          borderRadius: 0,
                          border: "1px solid rgb(221, 221, 221)",
                        }}
                        value={guestAddress.city}
                        onChange={(e) =>
                          setGuestAddress((p) => ({
                            ...p,
                            city: e.target.value,
                          }))
                        }
                      />
                      {guestErrors.city && (
                        <span style={{ color: "#c62828", fontSize: 12 }}>
                          {guestErrors.city}
                        </span>
                      )}
                    </fieldset>
                    <fieldset className="fieldset">
                      <label>State *</label>
                      <select
                        value={guestAddress.state}
                        onChange={(e) =>
                          setGuestAddress((p) => ({
                            ...p,
                            state: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          height: 50,
                          border: "1px solid #ddd",
                          padding: "10px 12px",
                          color: "#2c3a34",
                          background: "#fff",
                        }}
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      {guestErrors.state && (
                        <span style={{ color: "#c62828", fontSize: 12 }}>
                          {guestErrors.state}
                        </span>
                      )}
                    </fieldset>
                    <fieldset className="fieldset">
                      <label>PIN Code *</label>
                      <input
                        type="text"
                        maxLength={6}
                        style={{
                          borderRadius: 0,
                          border: "1px solid rgb(221, 221, 221)",
                        }}
                        value={guestAddress.pincode}
                        onChange={(e) =>
                          setGuestAddress((p) => ({
                            ...p,
                            pincode: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                      />
                      {guestErrors.pincode && (
                        <span style={{ color: "#c62828", fontSize: 12 }}>
                          {guestErrors.pincode}
                        </span>
                      )}
                    </fieldset>
                  </div>
                  <br />
                  <div className="mb_24">
                    <h6 className="fw-5 mb_20">Additional information</h6>
                    <fieldset className="fieldset">
                      <label>
                        Order notes{" "}
                        <span style={{ color: "#999", fontWeight: 400 }}>
                          (optional)
                        </span>
                      </label>
                      <textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notes about your order, e.g. special notes for delivery."
                        style={{
                          resize: "none",
                          width: "100%",
                          border: "1px solid #ddd",
                          padding: "12px 14px",
                          color: "#2c3a34",
                        }}
                      />
                    </fieldset>
                  </div>

                  {/* Login link */}
                </>
              )}
            </div>

            {/* ── RIGHT: Order Summary ── */}
            <div className="tf-page-cart-footer">
              <div className="tf-cart-footer-inner">
                <h5 className="fw-5 mb_20">Your order</h5>
                <form className="tf-page-cart-checkout widget-wrap-checkout">
                  {/* Cart items */}
                  <ul className="wrap-checkout-product">
                    {loading ? (
                      <li className="checkout-product-item">
                        <div className="content">
                          <div className="info">
                            <p className="name">Loading cart...</p>
                          </div>
                        </div>
                      </li>
                    ) : orderItems.length > 0 ? (
                      orderItems.map((item) => (
                        <li className="checkout-product-item" key={item.id}>
                          <figure className="img-product">
                            <img
                              src={item.image}
                              alt={item.title}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src =
                                  "/assets/images/product-placeholder.svg";
                              }}
                            />
                            <span className="quantity">{item.quantity}</span>
                          </figure>
                          <div className="content">
                            <div className="info">
                              <p className="name">{item.title}</p>
                              {item.variant && (
                                <span className="variant">{item.variant}</span>
                              )}
                            </div>
                            <span className="price">
                              ₹
                              {(item.price * item.quantity).toLocaleString(
                                "en-IN",
                              )}
                            </span>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="checkout-product-item">
                        <div className="content">
                          <div className="info">
                            <p className="name">Your cart is empty.</p>
                          </div>
                        </div>
                      </li>
                    )}
                  </ul>

                  {/* Coupon */}
                  {couponApplied ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 0",
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <span
                        style={{
                          color: "#2e7d32",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        ✓ "{couponApplied.code}" applied — ₹
                        {Number(couponApplied.discount_amount).toLocaleString(
                          "en-IN",
                        )}{" "}
                        off!
                      </span>
                      <span
                        onClick={handleRemoveCoupon}
                        style={{
                          color: "#c62828",
                          fontWeight: 600,
                          cursor: "pointer",
                          fontSize: 13,
                        }}
                      >
                        Remove
                      </span>
                    </div>
                  ) : (
                    <div className="coupon-box">
                      <input
                        type="text"
                        placeholder="Discount code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleApplyCoupon()
                        }
                      />
                      <button
                        type="button"
                        className="tf-btn btn-sm radius-3 btn-fill btn-icon animate-hover-btn"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        style={{ opacity: couponLoading ? 0.7 : 1 }}
                      >
                        {couponLoading ? "Applying..." : "Apply"}
                      </button>
                    </div>
                  )}

                  {/* Totals breakdown */}
                  <div
                    style={{
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: 12,
                      marginTop: 4,
                    }}
                  >
                    <div className="d-flex justify-content-between pb_8">
                      <span className="text_black-2">Subtotal</span>
                      <span>₹{subtotal.toLocaleString("en-IN")}</span>
                    </div>
                    {discount > 0 && (
                      <div className="d-flex justify-content-between pb_8">
                        <span style={{ color: "#2e7d32" }}>Discount</span>
                        <span style={{ color: "#2e7d32" }}>
                          − ₹{discount.toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between pb_8">
                      <span className="text_black-2">Shipping</span>
                      <span
                        style={{
                          color: shipping === 0 ? "#2e7d32" : "#2c3a34",
                        }}
                      >
                        {shipping === 0
                          ? "FREE"
                          : `₹${shipping.toLocaleString("en-IN")}`}
                      </span>
                    </div>
                    {subtotal > 0 && subtotal < 499 && (
                      <p
                        style={{ fontSize: 12, color: "#888", marginBottom: 8 }}
                      >
                        Add ₹{(499 - subtotal).toLocaleString("en-IN")} more for
                        free shipping!
                      </p>
                    )}
                    {paymentMethod === "cod" && (
                      <div className="d-flex justify-content-between pb_8">
                        <span className="text_black-2">
                          COD convenience charge
                        </span>
                        <span>₹{COD_CHARGE.toLocaleString("en-IN")}</span>
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between line pb_20">
                    <h6 className="fw-5">Total</h6>
                    <h6 className="total fw-5">
                      ₹{total.toLocaleString("en-IN")}
                    </h6>
                  </div>

                  {/* Payment method selection */}
                  <div
                    className="mb_20"
                    style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16 }}
                  >
                    <div className="fw-5 mb_12">Payment method</div>
                    <div className="d-flex flex-column gap-10">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          border: `2px solid ${paymentMethod === "prepaid" ? "#2c3a34" : "#e7e7e7"}`,
                          borderRadius: 10,
                          cursor: "pointer",
                          background:
                            paymentMethod === "prepaid" ? "#f8f8f6" : "#fff",
                        }}
                      >
                        <input
                          type="radio"
                          checked={paymentMethod === "prepaid"}
                          onChange={() => setPaymentMethod("prepaid")}
                          style={{ accentColor: "#2c3a34" }}
                        />
                        <span>
                          <strong>Pay Online</strong>
                          <span
                            className="text_black-2"
                            style={{ fontSize: 13, display: "block" }}
                          >
                            Credit / Debit card, UPI, Net Banking via Razorpay
                          </span>
                        </span>
                      </label>

                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          border: `2px solid ${paymentMethod === "cod" ? "#2c3a34" : "#e7e7e7"}`,
                          borderRadius: 10,
                          cursor: "pointer",
                          background:
                            paymentMethod === "cod" ? "#f8f8f6" : "#fff",
                        }}
                      >
                        <input
                          type="radio"
                          checked={paymentMethod === "cod"}
                          onChange={() => setPaymentMethod("cod")}
                          style={{ accentColor: "#2c3a34" }}
                        />
                        <span>
                          <strong>Cash on Delivery</strong>
                          <span
                            className="text_black-2"
                            style={{ fontSize: 13, display: "block" }}
                          >
                            Pay when your order arrives · ₹{COD_CHARGE}{" "}
                            convenience charge applies
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="wd-check-payment">
                    {paymentMethod === "prepaid" && (
                      <p className="text_black-2 mb_20">
                        💳 You will be redirected to Razorpay to complete your
                        payment securely.
                      </p>
                    )}
                    {paymentMethod === "cod" && (
                      <p className="text_black-2 mb_20">
                        🚚 Pay ₹{total.toLocaleString("en-IN")} in cash when
                        your order is delivered.
                      </p>
                    )}
                    <button
                      type="button"
                      className="tf-btn radius-3 btn-fill btn-icon animate-hover-btn justify-content-center w-100"
                      onClick={handlePlaceOrder}
                      disabled={placingOrder}
                      style={{
                        opacity: placingOrder ? 0.7 : 1,
                        cursor: placingOrder ? "not-allowed" : "pointer",
                      }}
                    >
                      {placingOrder
                        ? paymentMethod === "cod"
                          ? "Placing order..."
                          : "Redirecting..."
                        : "Place order"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Add Address Modal ── */}
      {showAddressModal && (
        <div
          onClick={() => setShowAddressModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(560px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 24px 70px rgba(0,0,0,0.14)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb_24">
              <div>
                <h5 className="fw-5 mb_5">Add address</h5>
                <p className="text_black-2 mb-0">
                  Save a delivery address for faster checkout.
                </p>
              </div>
              <button
                type="button"
                className="tf-btn btn-line"
                onClick={() => setShowAddressModal(false)}
              >
                Close
              </button>
            </div>

            <form className="form-checkout" onSubmit={saveAddress}>
              <div className="box grid-2">
                <fieldset className="fieldset">
                  <label>Name </label>
                  <input
                    type="text"
                    name="name"
                    value={addressForm.name}
                    onChange={handleAddressChange}
                  />
                  {addressErrors.name && (
                    <span style={{ color: "#c62828", fontSize: 12 }}>
                      {addressErrors.name}
                    </span>
                  )}
                </fieldset>
                <fieldset className="fieldset">
                  <label>Phone </label>
                  <input
                    type="tel"
                    name="phone"
                    maxLength={10}
                    value={addressForm.phone}
                    onChange={handleAddressChange}
                  />
                  {addressErrors.phone && (
                    <span style={{ color: "#c62828", fontSize: 12 }}>
                      {addressErrors.phone}
                    </span>
                  )}
                </fieldset>
              </div>

              <fieldset className="box fieldset">
                <label>Address Line 1 </label>
                <input
                  type="text"
                  name="address_line1"
                  value={addressForm.address_line1}
                  onChange={handleAddressChange}
                  placeholder="House number and street name"
                />
                {addressErrors.address_line1 && (
                  <span style={{ color: "#c62828", fontSize: 12 }}>
                    {addressErrors.address_line1}
                  </span>
                )}
              </fieldset>

              <fieldset className="box fieldset">
                <label>Address Line 2</label>
                <input
                  type="text"
                  name="address_line2"
                  value={addressForm.address_line2}
                  onChange={handleAddressChange}
                  placeholder="Apartment, suite, unit etc. (optional)"
                />
              </fieldset>

              <div className="box grid-3">
                <fieldset className="fieldset">
                  <label>City </label>
                  <input
                    type="text"
                    name="city"
                    value={addressForm.city}
                    onChange={handleAddressChange}
                  />
                  {addressErrors.city && (
                    <span style={{ color: "#c62828", fontSize: 12 }}>
                      {addressErrors.city}
                    </span>
                  )}
                </fieldset>
                <fieldset className="fieldset">
                  <label>State </label>
                  <select
                    name="state"
                    value={addressForm.state}
                    onChange={handleAddressChange}
                    style={{
                      width: "100%",
                      height: 50,
                      borderRadius: 0,
                      border: "1px solid #ddd",
                      padding: "10px 12px",
                      color: "#2c3a34",
                      background: "#fff",
                    }}
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {addressErrors.state && (
                    <span style={{ color: "#c62828", fontSize: 12 }}>
                      {addressErrors.state}
                    </span>
                  )}
                </fieldset>
                <fieldset className="fieldset">
                  <label>PIN Code </label>
                  <input
                    type="text"
                    name="pincode"
                    maxLength={6}
                    value={addressForm.pincode}
                    onChange={handleAddressChange}
                  />
                  {addressErrors.pincode && (
                    <span style={{ color: "#c62828", fontSize: 12 }}>
                      {addressErrors.pincode}
                    </span>
                  )}
                </fieldset>
              </div>

              <fieldset className="box fieldset">
                <label
                  className="d-flex align-items-center gap-10"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={addressForm.is_default}
                    onChange={handleAddressChange}
                  />
                  <span>Set as default address</span>
                </label>
              </fieldset>

              <button
                type="submit"
                className="tf-btn btn-fill animate-hover-btn justify-content-center w-100"
                disabled={savingAddress}
                style={{
                  opacity: savingAddress ? 0.7 : 1,
                  cursor: savingAddress ? "not-allowed" : "pointer",
                }}
              >
                {savingAddress ? "Saving..." : "Save address"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckoutV2;
